// Shared feedback parsers used by practice.tsx and interactive.tsx.
// Logic moved verbatim from practice.tsx — do not change behavior.

export type CeilingFlags = {
  OVERALL_CEILING?: string;
  CONTENT_GATE?: string;
  EXERCISE_CAP?: string;
};

export function parseV2Scores(raw: string): {
  dimensionScores: Record<string, number>;
  ceilingFlags: CeilingFlags;
  overallTrailer: number | null;
  bucket: string | null;
  finalScore: number | null;
} {
  const dimensionScores: Record<string, number> = {};

  const allRows = raw.split("\n").filter((r) => r.trim().startsWith("|"));
  const headerIdx = allRows.findIndex((r) =>
    /dimension/i.test(r) && /score/i.test(r)
  );
  if (headerIdx >= 0) {
    for (let i = headerIdx + 1; i < allRows.length; i++) {
      const row = allRows[i];
      const cells = row.split("|").map((c) => c.trim()).filter(Boolean);
      if (cells.length < 2) continue;
      if (/^[-:]+$/.test(cells[0])) continue;
      const name = cells[0].replace(/\*\*/g, "").trim();
      if (!name || /^dimension$/i.test(name)) continue;
      const scoreMatch = cells[1].match(/^(\d+(?:\.\d+)?)/);
      if (!scoreMatch) continue;
      const v = parseFloat(scoreMatch[1]);
      if (isNaN(v) || v < 1 || v > 5) continue;
      dimensionScores[name] = v;
    }
  }

  const trailerMatch = raw.match(/\[SCORES\]\s+OVERALL=([\d.]+)\s+BUCKET=(\S+)/i);
  const overallTrailer = trailerMatch ? parseFloat(trailerMatch[1]) : NaN;
  const bucket = trailerMatch ? trailerMatch[2] : null;

  const ceilingFlags: CeilingFlags = {};
  const cfMatch = raw.match(/CEILING_FLAGS:\s*([^\n]+)/i);
  if (cfMatch) {
    for (const part of cfMatch[1].split(",").map((p) => p.trim())) {
      const eq = part.indexOf("=");
      if (eq > 0) {
        const k = String(part.slice(0, eq).trim());
        const v = String(part.slice(eq + 1).trim());
        if (k === "OVERALL_CEILING" || k === "CONTENT_GATE" || k === "EXERCISE_CAP") {
          ceilingFlags[k as keyof CeilingFlags] = v;
        }
      }
    }
  }

  const dimValues = Object.values(dimensionScores);
  let computed: number | null =
    dimValues.length > 0
      ? Math.round((dimValues.reduce((a, b) => a + b, 0) / dimValues.length) * 2) / 2
      : isNaN(overallTrailer) ? null : overallTrailer;

  if (computed != null) {
    if (ceilingFlags.OVERALL_CEILING === "2.5") computed = Math.min(computed, 2.5);
    if (ceilingFlags.CONTENT_GATE === "TRUE") computed = Math.min(computed, 3.0);
    if (ceilingFlags.EXERCISE_CAP && ceilingFlags.EXERCISE_CAP !== "NONE") {
      const capVal = parseFloat(ceilingFlags.EXERCISE_CAP.split("=")[1] ?? "");
      if (!isNaN(capVal)) computed = Math.min(computed, capVal);
    }
  }

  return {
    dimensionScores,
    ceilingFlags,
    overallTrailer: isNaN(overallTrailer) ? null : overallTrailer,
    bucket,
    finalScore: computed,
  };
}

export function stripScoreBlocks(text: string): string {
  const result = String(text ?? "")
    .replace(/<internal_scoring>[\s\S]*?<\/internal_scoring>/gi, "")
    .replace(/##\s*SCORE[\s\S]*?(?=\n(?:#{1,4}\s*)?(?:\*{0,2})\s*[A-Za-z]\s*[—–\-]|\n\[SCORES|$)/i, "")
    .replace(/CEILING_FLAGS:[^\n]*/gi, "")
    .replace(/^\s*\[SCORES\b[^\n]*$/gm, "")
    .replace(/\*{0,2}CANDIDATE FEEDBACK:\*{0,2}\s*/gi, "")
    .replace(/---\s*\nMODEL RESPONSE[\s\S]*$/gi, "")
    .replace(/^\s*[\[\]]\s*$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  return result ?? "";
}

export type ClearKey = "C" | "L" | "E" | "A" | "R";
export type ClearSection = {
  key: ClearKey;
  label: string;
  color: string;
  content: string;
};

export function parseCLEAR(fb: string): ClearSection[] {
  const meta: Record<ClearKey, { label: string; color: string }> = {
    C: { label: "Commend", color: "#1A5C3A" },
    L: { label: "Limitations", color: "#B8956A" },
    E: { label: "Examples", color: "#4A6FA5" },
    A: { label: "Action", color: "#B8956A" },
    R: { label: "Rank", color: "rgba(184,149,106,0.6)" },
  };

  const SECTION_FALLBACKS: Record<ClearKey, string> = {
    C: "No specific strength identified — submit a longer response for behavioral feedback.",
    L: "No limitation identified — submit a longer response for targeted coaching.",
    E: "No example provided — submit a longer response to see what a 4/5 and 5/5 looks like.",
    A: "On your next rep, identify the single highest-leverage behavioral change and state it as a specific, observable action you will take differently.",
    R: "Score not available — response was too brief to evaluate against the rubric.",
  };

  const cleanText = fb
    .replace(/\[SCORES\][^\n]*/gi, "")
    .replace(/CEILING_FLAGS:[^\n]*/gi, "")
    .replace(/^\s*---\s*$/gm, "")
    .replace(/\*{0,2}My gap is:\*{0,2}[^\n]*/gi, "")
    .replace(/\*{0,2}My prescription is:\*{0,2}[^\n]*/gi, "")
    .replace(/MODEL RESPONSE[\s\S]*$/gi, "")
    .replace(/CANDIDATE FEEDBACK:\s*/gi, "");

  const normalizedText = cleanText
    .replace(/^(?!##)\s*\*{0,2}C\s*[—–-]\s*\w+[:\*]*/gm, "## C — COMMEND")
    .replace(/^(?!##)\s*\*{0,2}L\s*[—–-]\s*\w+[:\*]*/gm, "## L — LIMITATIONS")
    .replace(/^(?!##)\s*\*{0,2}E\s*[—–-]\s*\w+[:\*]*/gm, "## E — EXAMPLES")
    .replace(/^(?!##)\s*\*{0,2}A\s*[—–-]\s*\w+[:\*]*/gm, "## A — ACTION")
    .replace(/^(?!##)\s*\*{0,2}R\s*[—–-]\s*\w+[:\*]*/gm, "## R — RANK");

  const patterns: Record<ClearKey, RegExp> = {
    C: /^##\s*C\s*[—–-]\s*COMMEND/m,
    L: /^##\s*L\s*[—–-]\s*LIMITATIONS?/m,
    E: /^##\s*E\s*[—–-]\s*EXAMPLES?/m,
    A: /^##\s*A\s*[—–-]\s*ACTION/m,
    R: /^##\s*R\s*[—–-]\s*RANK/m,
  };

  const keys: ClearKey[] = ["C", "L", "E", "A", "R"];
  const indices: Record<string, number> = {};
  for (const k of keys) {
    indices[k] = normalizedText.search(patterns[k]);
  }

  const found = keys
    .filter((k) => indices[k] !== -1)
    .sort((a, b) => indices[a] - indices[b]);

  const extractSection = (key: ClearKey) => {
    const idx = indices[key];
    if (idx === -1) return "";
    const nextKey = found.find((k) => k !== key && indices[k] > idx);
    const end = nextKey ? indices[nextKey] : normalizedText.length;
    const slice = normalizedText.slice(idx, end);
    return slice.replace(patterns[key], "").replace(/\*\*/g, "").trim();
  };

  return keys.map((k) => {
    let content = extractSection(k);
    if (!content || content.length < 10) {
      content = SECTION_FALLBACKS[k];
    }
    return { key: k, label: meta[k].label, color: meta[k].color, content };
  });
}
